import React, {
	// eslint-disable-next-line jira/restricted/react-component-props
	type ComponentProps,
	type ComponentPropsWithoutRef,
	memo,
	useMemo,
	useCallback,
	useState,
	forwardRef,
	type ReactNode,
} from 'react';
import { styled as styled2 } from '@compiled/react';
// eslint-disable-next-line jira/restricted/styled-components-migration, @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from 'styled-components';
import Button from '@atlaskit/button';
import type { JSONDocNode } from '@atlaskit/editor-json-transformer';
import AkPopup, { type TriggerProps } from '@atlaskit/popup';
import { xcss, Pressable } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { layers, gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useCard } from '@atlassian/jira-issue-card-service/src/services/use-card/index.tsx';
import Card from './card/index.tsx';
import messages from './messages.tsx';

type FieldIconPopupPropsWithAdfDescription = {
	children: ReactNode;
	adfDescription: JSONDocNode;
	/**
	 * This will add an aria-label to the icon, to have no aria label at all pass an empty string
	 * The aria-label will be in the format "More information about {label}"
	 * Would be good to rename this to ariaLabelPostfix or something similar in the future.
	 */
	label: string;
};

type FieldIconPopupPropsWithCustomContent = {
	children: ReactNode;
	customContent: ReactNode;
	/**
	 * This will add an aria-label to the icon, to have no aria label at all pass an empty string
	 * The aria-label will be in the same format as passed.
	 * Would be good to rename this to ariaLabelPostfix or something similar in the future.
	 */
	label: string;
};

type FieldIconPopupProps =
	| FieldIconPopupPropsWithAdfDescription
	| FieldIconPopupPropsWithCustomContent;

// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
const containerHeight = window.innerHeight < 600 ? 150 : 400;

// This is visible for testing
export const useIsOpen = () => useState(false);

export const FieldIconPopup = memo<FieldIconPopupProps>(
	({ children, label, ...props }: FieldIconPopupProps) => {
		let adfDescription: JSONDocNode;
		let customContent: ReactNode;
		if ('adfDescription' in props) {
			adfDescription = props.adfDescription;
		} else if ('customContent' in props) {
			customContent = props.customContent;
		}
		const [isOpen, setIsOpen] = useIsOpen();
		const [autoFocus, setAutoFocus] = useState(false);
		const { formatMessage } = useIntl();
		const onHide = () => setIsOpen(false);
		const onShow = () => setIsOpen(true);

		const { setCardHoverStatus, setTriggerHoverStatus } = useCard({
			onHide,
			onShow,
		});

		const handleMouseEnter = useCallback(() => {
			setAutoFocus(false);
			setTriggerHoverStatus(true);
		}, [setTriggerHoverStatus]);
		const handleMouseLeave = useCallback(() => {
			setTriggerHoverStatus(false);
		}, [setTriggerHoverStatus]);
		const onMouseEnterContent = useCallback(() => {
			setAutoFocus(false);
			setCardHoverStatus(true);
		}, [setCardHoverStatus]);
		const onMouseLeaveContent = useCallback(() => {
			setCardHoverStatus(false);
		}, [setCardHoverStatus]);
		const handleTriggerClick = useCallback(() => {
			setTriggerHoverStatus(true);
			setAutoFocus(true);
		}, [setTriggerHoverStatus]);
		const onPopupClose = useCallback(() => {
			setIsOpen(false);
			setAutoFocus(false);
		}, [setIsOpen]);

		// FIXME: Bento should fix that by extracting the forwarded component
		const CustomPopupContainer = useMemo(
			() =>
				// eslint-disable-next-line react/no-unstable-nested-components
				forwardRef<HTMLDivElement, ComponentProps<typeof PopupCustomContainer>>(
					(containerProps, ref) => (
						<PopupCustomContainer
							{...containerProps}
							ref={ref}
							containerHeight={containerHeight}
							onMouseEnter={onMouseEnterContent}
							onMouseLeave={onMouseLeaveContent}
							onClick={(e) => e.stopPropagation()}
						/>
					),
				),
			[onMouseEnterContent, onMouseLeaveContent],
		);

		const fieldIconPopupTriggerWrapperOld = (triggerProps: TriggerProps) => (
			<Button
				aria-label={customContent ? label : formatMessage(messages.moreInformation, { label })}
				{...triggerProps}
				onClick={handleTriggerClick}
				spacing="none"
				appearance="subtle"
				isSelected={isOpen}
				data-component-selector={fieldIconPopupWithButtonSelectorName}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				interactionName="auto-generated.jira-issue-field-icon-popup-button"
			>
				{children}
			</Button>
		);

		const fieldIconPopupTriggerWrapperNew = (triggerProps: TriggerProps) => (
			<Pressable
				aria-label={formatMessage(messages.moreInformation, { label })}
				{...triggerProps}
				onClick={handleTriggerClick}
				data-component-selector={fieldIconPopupWithButtonSelectorName}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				xcss={fieldIconPopupTriggerWrapperNewStyles}
				interactionName="auto-generated.jira-issue-field-icon-popup.button"
			>
				{children}
			</Pressable>
		);

		const fieldIconPopupTriggerWrapper = fg('issue_view_field_config_edit')
			? fieldIconPopupTriggerWrapperNew
			: fieldIconPopupTriggerWrapperOld;

		return (
			<AkPopup
				zIndex={layers.modal}
				autoFocus={autoFocus}
				isOpen={isOpen}
				onClose={onPopupClose}
				content={() => customContent || <Card description={adfDescription} />}
				trigger={fieldIconPopupTriggerWrapper}
				placement="top"
				// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<RefAttributes<unknown>>' is not assignable to type 'ComponentType<PopupComponentProps> | undefined'.
				popupComponent={CustomPopupContainer}
				boundary="clippingParents"
				shouldFlip
				testId="issue-field-icon-popup.ak-popup"
			/>
		);
	},
);

const fieldIconPopupWrapperSelectorName = 'jira-issue-field-icon-popup-wrapper';
export const FIELD_ICON_POPUP_WRAPPER_COMPONENT_SELECTOR = `[data-component-selector="${fieldIconPopupWrapperSelectorName}"]`;

export const fieldIconPopupWithButtonSelectorName =
	'jira-issue-field-icon-popup-with-button-wrapper';
const FIELD_ICON_POPUP_WITH_BUTTON_WRAPPER_COMPONENT_SELECTOR = `[data-component-selector="${fieldIconPopupWithButtonSelectorName}"]`;

export const FieldIconPopupWrapper = (
	props: ComponentPropsWithoutRef<typeof FieldIconPopupWrapperComponent>,
) => (
	<FieldIconPopupWrapperComponent
		data-component-selector={fieldIconPopupWrapperSelectorName}
		{...props}
	/>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PopupCustomContainer = styled2.div<{
	containerHeight: number;
}>({
	paddingTop: token('space.200'),
	paddingRight: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.300'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	maxHeight: (props) => `${props.containerHeight}px`,
	overflow: 'auto',
	backgroundColor: token('elevation.surface.raised'),
	boxShadow: token('elevation.shadow.raised'),
});

/**
 * @deprecated `FieldIconPopupWrapperControl` is used as component selector in `fieldHeadingIconsCss`
 * which is consumed by other styled-components (e.g. src/packages/servicedesk/post-incident-review/src/ui/primary-incident/styled.tsx )
 * TODO: Remove `FieldIconPopupWrapperControl` once its consumers are migrated to CompiledCSS
 */
// eslint-disable-next-line  @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FieldIconPopupWrapperControl = styled.div({
	color: token('color.background.neutral.bold'),
	cursor: 'pointer',
	marginLeft: token('space.050'),
	flexShrink: 0,
	'&:hover': {
		color: token('color.background.neutral.bold.hovered'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldIconPopupWrapperComponentOld = styled2.div({
	color: token('color.background.neutral.bold'),
	cursor: 'pointer',
	marginLeft: token('space.050'),
	flexShrink: 0,
	'&:hover': {
		color: token('color.background.neutral.bold.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`${FIELD_ICON_POPUP_WITH_BUTTON_WRAPPER_COMPONENT_SELECTOR}`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':focus, :hover': {
			background: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':focus svg, :hover svg': {
			color: `${token('color.background.neutral.bold.hovered')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			color: `${token('color.background.neutral.bold')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'[data-testid="issue-field-description.ui.assets.icon"]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			'padding-top': `${gridSize * 0.25}px`,
		},
		background: 'none',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldIconPopupWrapperComponentNew = styled2.div({
	color: token('color.background.neutral.bold'),
	cursor: 'pointer',
	marginLeft: token('space.050'),
	flexShrink: 0,
	paddingTop: token('space.025'),
	'&:hover': {
		color: token('color.background.neutral.bold.hovered'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	[`${FIELD_ICON_POPUP_WITH_BUTTON_WRAPPER_COMPONENT_SELECTOR}`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
		':focus svg, :hover svg': {
			color: `${token('color.background.neutral.bold.hovered')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		svg: {
			color: `${token('color.background.neutral.bold')}`,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
		'[data-testid="issue-field-description.ui.assets.icon"]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
			'padding-top': `${gridSize * 0.25}px`,
		},
	},
});

const FieldIconPopupWrapperComponent = componentWithFG(
	'issue_view_field_config_edit',
	FieldIconPopupWrapperComponentNew,
	FieldIconPopupWrapperComponentOld,
);

const fieldIconPopupTriggerWrapperNewStyles = xcss({
	display: 'flex',
	width: '16px',
	height: '16px',
	padding: 'space.025',
	borderRadius: '4px',
	justifyContent: 'center',
	alignItems: 'center',
	backgroundColor: 'color.background.neutral.subtle',
	color: 'color.text.subtle',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});
